import moment from 'moment';

import AppUtils from './AppUtils';

import { PERIODS } from '../constants/Common';

const MIDNIGHT_REGEX = /^00:(\d)/;

const REGEXES = {
    time: {
        ISO8601: [
            [/[^\d:]/, ''],
            [/^[^0-2]/, ''],
            [/^([2-9])[4-9]/, '$1'],
            [/^\d[:]/, ''],
            [/^([01][0-9]|2[0-3])[^:0-5]/, '$1:'],
            [/^(\d{2})(\d)/, '$1:$2'],
            [/^(\d{2}[:])[^0-5]/, '$1'],
            [/^(\d{2}:[0-5])[^0-9]/, '$1'],
            [/^(\d{2}:\d[0-9])./, '$1']
        ],
        USA: [
            [/[^\d:]/, ''],
            [/^[^0-9]/, ''],
            [/^([2-9])/, '0$1'],
            [/^(0)[^:1-9]|(1)[^:0-9]/, '$1'],
            [/^(\d)[:]/, '$1'],
            [/^([01][1-9])[^:0-5]/, '$1'],
            [/^(\d{2})(\d)/, '$1:$2'],
            [/^(\d{2}[:])[^0-5]/, '$1'],
            [/^(\d{2}:[0-5])[^0-9]/, '$1'],
            [/^(\d{2}:\d[0-9])./, '$1']
        ],
    },
    date: {
        ISO8601: [
            [/[^\d/]/, ''],
            [/^([^0-3])/, '0$1'],
            [/^(0)[^1-9]/, '$1'],
            [/^(3)[^0-1]/, '$1'],
            [/^(\d{2})(\d)/, '$1/$2'],
            [/^(\d{2}\/0)0/, '$1'],
            [/^(\d{2})\/([^0-1])/, '$1/0$2'],
            [/^(\d{2}\/1)[^0-2]/, '$1'],
            [/^(\d{2}\/\d{2})(\d)/, '$1/$2'],
            [/^(\d{2}\/\d{2}\/)[^1-2]/, '$1'],
            [/^(\d{2}\/\d{2}\/1)[^9]/, '$1'],
            [/^(\d{2}\/\d{2}\/2)[^0]/, '$1'],
            [/^(\d{2}\/\d{2}\/\d{4})./, '$1'],
        ],
        USA: [
            [/[^\d/]/, ''],
            [/^([^0-1])/, '0$1'],
            [/^00/, '0'],
            [/^(1)[^0-2]/, '$1'],
            [/^(\d{2})(\d)/, '$1/$2'],
            [/^(\d{2}\/)([^0-3])/, '$10$2'],
            [/^(\d{2}\/0)[^1-9]/, '$1'],
            [/^(\d{2}\/3)[^0-1]/, '$1'],
            [/^(\d{2}\/\d{2})(\d)/, '$1/$2'],
            [/^(\d{2}\/\d{2}\/)[^1-2]/, '$1'],
            [/^(\d{2}\/\d{2}\/1)[^9]/, '$1'],
            [/^(\d{2}\/\d{2}\/2)[^0]/, '$1'],
            [/^(\d{2}\/\d{2}\/\d{4})./, '$1'],
        ],
    }
};

const THIRTY_DAY_MONTHS = [4, 6, 9, 11];

const Masks = {
    convert12to24(time, period, useUSFormat) {
        if (!useUSFormat) return time;

        const timeRegex = /(\d{2}):(\d{2})/g;

        const timeResult = timeRegex.exec(time);
        const hourAddition = period === PERIODS.AM ? 0 : 12;

        const hour = `${parseInt(timeResult[1]) + hourAddition}`.padStart(2, '0');
        const minutes = timeResult[2].padStart(2, '0');

        return !!hour ? `${hour}:${minutes}` : '';
    },

    convert24to12(time, period, useUSFormat) {
        const isApp = AppUtils.releaseValidate(232);
        const hour = parseInt(time.slice(0, 2));

        if (!useUSFormat) return [time, period];

        if (time.length < 5 || hour <= 12) {
            return [time, hour === 12 ? 'PM' : 'AM'];
        }

        if (isApp) return [time, 'PM'];

        const hourStr = `0${hour - (isApp ? 0 : 12)}`.slice(-2);

        return [`${hourStr}:${time.slice(3, 5)}`, 'PM'];
    },

    time(time, period, useUSFormat = false) {
        if (!time) return '';
    
        try {
            const isApp = AppUtils.releaseValidate(232);

            const regexes = useUSFormat && !isApp
                ? REGEXES.time.USA
                : REGEXES.time.ISO8601;

            const [_time, _period] = this.convert24to12(time, period, useUSFormat);

            const newTime = regexes.reduce((prev, crr) => {
                if (typeof prev !== 'string') {
                    return _time.replace(...prev).replace(...crr);
                }

                return prev.replace(...crr);
            });

            if (useUSFormat && MIDNIGHT_REGEX.test(newTime)) {
                return [newTime, 'AM'];
            }
        
            return [newTime, _period];
        } catch (error) {
            return time;
        }
    },

    validateMonth(date, useUSFormat = false) {
        if (!date || date.length !== 5) return date;

        const strMonth = date.split('/')[useUSFormat ? 0 : 1];
        const month = parseInt(strMonth);
        const day = parseInt(date.split('/')[useUSFormat ? 1 : 0]);
        const isThirtyDayMonth = THIRTY_DAY_MONTHS.indexOf(month) > -1;

        if (month === 2 && day > 29) {
            return useUSFormat ? '02/29' : '29/02';
        }

        if (isThirtyDayMonth && day > 30) {
            return useUSFormat ? `${strMonth}/30` : `30/${strMonth}`;
        }

        return date;
    },

    validateLeapYear(date, isISO8601 = true, useUsFormat = false) {
        const dateStr = isISO8601 ? date : this.commonToISO8601(date, useUsFormat);
        const isValid = moment(dateStr).isValid();

        if (isValid) return date;
        
        const yearStr = dateStr.split('-')[0];
        const isLeapYear = moment(yearStr).isLeapYear();
        
        return !(isValid && isLeapYear) ? `${yearStr}-02-28` : dateStr;
    },

    date(date, useUSFormat = false) {
        const isString = typeof date === 'string';
        const isNumber = typeof date === 'number';
    
        if (parseInt(date) < 0 || (isString && !date) || (!isString && !isNumber)) return '';
        
        const dateString = `${date}`;
    
        const regexes = useUSFormat ? REGEXES.date.USA : REGEXES.date.ISO8601;
    
        const newDateString = regexes.reduce((prev, crr) => {
            if (dateString.length === 5) {
                const validMonth = this.validateMonth(dateString, useUSFormat);
                return validMonth.replace(...crr);
            }

            if (typeof prev !== 'string') {
                return dateString.replace(...prev).replace(...crr);
            }
    
            return prev.replace(...crr);
        });

        if (newDateString.length !== 10) return newDateString;
        
        const valid = this.validateLeapYear(date, false, useUSFormat);

        return this.iso8601ToCommon(valid, useUSFormat);
    },

    commonToISO8601(date, useUsFormat = false) {
        if (date.length !== 10) return date;
        
        const replace = useUsFormat ? '$3-$1-$2' : '$3-$2-$1';

        return date.replace(/^(\d{2})\/(\d{2})\/(\d{4})$/, replace);
    },

    iso8601ToCommon(date, useUsFormat = false) {
        if (date.length !== 10) return date;
        
        const replace = useUsFormat ? '$2/$3/$1' : '$3/$2/$1';
        
        return date.replace(/^(\d{4})-(\d{2})-(\d{2})$/, replace);
    }
};

export default Masks;